import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState, useRef } from "react";
import { useToastStore } from "&/toast";
import { privateHttp } from "../../../../api/config/axios";
import { useHistoryBackGuard } from "^/common/useHistoryBackGuard";

export const Route = createFileRoute("/customer/layout/payment/success")({
  component: PaymentSuccess,
});

// 이미 order-confirm 이 끝난 주문번호 표시용 키.
// 뒤로가기로 이 화면에 다시 들어와도 confirm 을 재호출하지 않기 위해 필요하다.
const confirmedOrderKey = (orderId: string) => `confirmedOrderId:${orderId}`;

function PaymentSuccess() {
  const { add } = useToastStore();
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(true);
  const [paymentInfo, setPaymentInfo] = useState<any>(null);
  /**
   * 가상계좌 입금 안내 — paymentStatus === "WAITING_DEPOSIT" 인 경우에만 채워진다.
   * 아직 이용권이 발급되지 않은 상태이므로 "결제 완료" 화면 대신 입금 안내를 띄운다.
   */
  const [depositInfo, setDepositInfo] = useState<any>(null);
  const [copied, setCopied] = useState(false);
  const hasRequestedRef = useRef(false);

  // 이 화면의 직전 히스토리 항목은 토스 결제창(외부 도메인)이다.
  // 그대로 두면 뒤로가기 시 "결제할 수 있는 시간이 지났어요" 만료 화면으로 빠져
  // 앱으로 되돌아올 방법이 없어지므로, 뒤로가기를 홈으로 돌린다.
  useHistoryBackGuard(() => {
    navigate({ to: "/customer/layout/home", replace: true });
  });

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const orderId = urlParams.get("orderId");
    const paymentKey = urlParams.get("paymentKey");
    const amount = urlParams.get("amount");
    const companySeq = urlParams.get("companySeq");
    const restoreState = urlParams.get("restoreState");

    // 🔥 paymentKey 누락 가능성 처리
    if (!paymentKey) {
      console.error("❌ paymentKey 누락 - 상세 정보:", {
        orderId,
        amount,
        fullUrl: window.location.href,
        search: window.location.search,
        allParams: Object.fromEntries(urlParams.entries()),
      });

      // orderId가 있으면 결제 상태 조회 API 호출 시도 (대안)
      if (orderId) {
        const amountValue = amount ? parseInt(amount) : 0;
        checkPaymentStatusByOrderId(orderId, amountValue);
        return;
      }
      add("결제 정보를 확인할 수 없습니다. 주문번호가 없습니다.");
      navigate({
        to: "/customer/layout/payment/fail",
        search: {
          code: "MISSING_PAYMENT_KEY",
          message: "결제 정보를 확인할 수 없습니다. 고객센터로 문의해주세요.",
        },
      });
      return;
    }

    // 필수 파라미터 검증 (paymentKey는 위에서 확인됨)
    if (!orderId || !amount) {
      console.error("❌ 필수 파라미터 누락:", {
        hasOrderId: !!orderId,
        hasAmount: !!amount,
        hasPaymentKey: !!paymentKey,
        fullUrl: window.location.href,
      });
      add("결제 정보가 불완전합니다. 주문번호 또는 결제금액이 없습니다.");
      navigate({
        to: "/customer/layout/payment/fail",
        search: {
          orderId: orderId || "",
          code: "MISSING_PARAMS",
          message: "결제 정보가 불완전합니다.",
        },
      });
      return;
    }

    // 결제 정보 저장
    setPaymentInfo({
      orderId,
      paymentKey,
      amount: parseInt(amount),
      companySeq: companySeq ? parseInt(companySeq) : null,
      restoreState: restoreState || null,
    });

    // 🔥 결제 완료 시 세션 스토리지 정리 및 완료 플래그 설정
    sessionStorage.removeItem("currentPaymentOrderId");
    sessionStorage.removeItem("currentPaymentData");
    sessionStorage.removeItem("paymentCanceled");
    // 🔥 결제 완료 플래그 설정 (payment/index.tsx에서 상태 해제용)
    sessionStorage.setItem("paymentCompleted", "true");

    // 뒤로가기로 재진입한 경우: 이미 확인이 끝난 주문이므로 confirm 을 다시 부르지 않는다.
    // (재호출하면 결제 실패 화면으로 튄다)
    if (sessionStorage.getItem(confirmedOrderKey(orderId)) === "1") {
      hasRequestedRef.current = true;
      setIsProcessing(false);
      return;
    }

    // 중복 요청 방지
    if (!hasRequestedRef.current) {
      hasRequestedRef.current = true;
      console.log("🔵 confirmOrder 호출 준비:", {
        paymentKey,
        orderId,
        amount: parseInt(amount),
      });
      confirmOrder(paymentKey, orderId, parseInt(amount));
    } else {
      console.warn("⚠️ confirmOrder 이미 호출됨 - 중복 요청 방지");
    }
  }, [navigate, add]);

  // 🔥 paymentKey 누락 시 orderId로 결제 상태 조회 (대안)
  const checkPaymentStatusByOrderId = async (
    orderId: string,
    fallbackAmount: number
  ) => {
    try {
      // 결제 상태 조회 API 호출 (백엔드에 해당 API가 있다고 가정)
      // 만약 없다면 이 부분은 제거하고 사용자에게 직접 안내
      const response = await privateHttp.get(
        `/user/main/ticket/payment-status/${orderId}`
      );

      const paymentData = response.data?.data;
      if (paymentData?.paymentKey) {
        // paymentKey를 찾았으면 정상 흐름으로 진행
        if (!hasRequestedRef.current) {
          hasRequestedRef.current = true;
          confirmOrder(
            paymentData.paymentKey,
            orderId,
            paymentData.amount || fallbackAmount
          );
        }
      } else {
        // paymentKey를 찾지 못한 경우
        console.error("❌ 결제 상태 조회에서도 paymentKey를 찾지 못함");
        add("결제 정보를 확인할 수 없습니다. 고객센터로 문의해주세요.");
        setIsProcessing(false);
        navigate({
          to: "/customer/layout/payment/fail",
          search: {
            orderId,
            code: "PAYMENT_KEY_NOT_FOUND",
            message: "결제 정보를 확인할 수 없습니다. 고객센터로 문의해주세요.",
          },
        });
      }
    } catch (error: any) {
      console.error("❌ 결제 상태 조회 실패:", error);

      // API가 없거나 실패한 경우 사용자에게 안내
      // (404 등은 API가 없는 경우일 수 있으므로 사용자에게 직접 안내)
      const errorStatus = error?.response?.status;
      if (errorStatus === 404) {
        add(
          "결제 정보를 확인할 수 없습니다. 주문번호를 확인하고 고객센터로 문의해주세요."
        );
      } else {
        add("결제 정보 조회에 실패했습니다. 고객센터로 문의해주세요.");
      }

      setIsProcessing(false);
      setPaymentInfo({
        orderId,
        paymentKey: null,
        amount: fallbackAmount,
        companySeq: null,
        restoreState: null,
        error: "paymentKey 누락",
      });

      navigate({
        to: "/customer/layout/payment/fail",
        search: {
          orderId,
          code: "PAYMENT_STATUS_CHECK_FAILED",
          message: "결제 정보를 확인할 수 없습니다. 고객센터로 문의해주세요.",
        },
      });
    }
  };

  const confirmOrder = async (
    paymentKey: string,
    orderId: string,
    amount: number
  ) => {
    try {
      // 이용권 구매 확인 API 엔드포인트
      const apiEndpoint = "/user/main/ticket/order-confirm";

      console.log("🔵 order-confirm API 호출 시작:", {
        apiEndpoint,
        paymentKey,
        orderId,
        amount,
      });

      const response = await privateHttp.post(apiEndpoint, {
        paymentKey,
        orderId,
        amount,
      });

      console.log("✅ order-confirm API 호출 성공:", response.data);

      // 가상계좌 — 계좌만 발급된 상태. 아직 이용권이 나온 게 아니므로 입금 안내를 띄운다.
      const first = response.data?.data?.[0];
      if (first?.paymentStatus === "WAITING_DEPOSIT") {
        setDepositInfo(first);
        setIsProcessing(false);
        return;
      }

      // 성공 시 UI 업데이트
      try {
        sessionStorage.setItem(confirmedOrderKey(orderId), "1");
      } catch (storageError) {
        console.error("주문 확인 기록 저장 실패:", storageError);
      }
      add("주문이 성공적으로 확인되었습니다!");
      setIsProcessing(false);
    } catch (error: any) {
      console.error("❌ order-confirm API 호출 실패:", error);
      console.error("❌ 에러 상세:", {
        status: error?.response?.status,
        statusText: error?.response?.statusText,
        data: error?.response?.data,
        message: error?.message,
      });

      // paymentKey 관련 에러인 경우 특별 처리
      if (error?.response?.status === 400 || error?.response?.status === 404) {
        const errorMessage = error?.response?.data?.message || "";
        if (
          errorMessage.includes("paymentKey") ||
          errorMessage.includes("결제")
        ) {
          add("결제 정보가 유효하지 않습니다. 고객센터로 문의해주세요.");
          navigate({
            to: "/customer/layout/payment/fail",
            search: {
              orderId,
              code: "INVALID_PAYMENT_KEY",
              message: "결제 정보가 유효하지 않습니다.",
            },
          });
          return;
        }
      }

      add("주문 확인에 실패했습니다.");
      navigate({
        to: "/customer/layout/payment/fail",
        search: {
          orderId,
          code: "ORDER_CONFIRM_FAILED",
          message: "주문 확인에 실패했습니다.",
        },
      });
    }
  };

  if (isProcessing) {
    return (
      <div className="flex justify-center items-center flex-auto">
        <div className="text-center">
          <div className="mb-4">
            <div className="mx-auto w-12 h-12 rounded-full border-b-2 border-orange-500 animate-spin"></div>
          </div>
          <div className="mb-2 text-lg font-semibold">
            결제를 처리하는 중...
          </div>
          <div className="text-gray-600">잠시만 기다려주세요.</div>
          <div className="mt-4 text-sm text-blue-600">
            🔍 주문 확인 API 호출 중...
          </div>
        </div>
      </div>
    );
  }

  // 가상계좌 입금 대기 — 결제창은 통과했지만 아직 입금 전이라 이용권이 없다.
  if (depositInfo) {
    const fmtDue = (iso?: string | null) => {
      if (!iso) return null;
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return null;
      const days = ["일", "월", "화", "수", "목", "금", "토"];
      const h = d.getHours();
      const ampm = h < 12 ? "오전" : "오후";
      const h12 = h % 12 === 0 ? 12 : h % 12;
      const mm = String(d.getMinutes()).padStart(2, "0");
      return `${d.getMonth() + 1}월 ${d.getDate()}일(${days[d.getDay()]}) ${ampm} ${h12}:${mm}`;
    };

    const copyAccount = async () => {
      if (!depositInfo.virtualAccountNumber) return;
      try {
        await navigator.clipboard.writeText(depositInfo.virtualAccountNumber);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch {
        // 앱뷰에서 clipboard 가 막힌 경우 — 길게 눌러 직접 복사
        setCopied(false);
      }
    };

    return (
      <div className="flex justify-center items-center flex-auto bg-gray-50">
        <div className="p-6 w-full max-w-md bg-white rounded-lg shadow-lg">
          <div className="mb-5 text-center">
            <div className="flex justify-center items-center mx-auto mb-3 w-16 h-16 bg-orange-100 rounded-full">
              <span className="text-3xl">🏦</span>
            </div>
            <h1 className="mb-2 text-2xl font-bold text-gray-900">입금 대기 중</h1>
            <p className="text-sm text-gray-600">
              아래 계좌로 입금하시면 이용권이 자동으로 발급돼요
            </p>
          </div>

          <div className="p-4 mb-4 rounded-lg border border-gray-200">
            <div className="text-xs text-gray-500">입금 계좌</div>
            <div className="flex gap-2 justify-between items-center mt-1">
              <div className="min-w-0">
                <div className="text-base font-bold text-gray-900">
                  {depositInfo.virtualAccountBankName ?? "-"}
                </div>
                <div className="mt-0.5 font-mono text-lg font-bold text-gray-900 break-all">
                  {depositInfo.virtualAccountNumber ?? "-"}
                </div>
              </div>
              <button
                type="button"
                onClick={copyAccount}
                className="shrink-0 px-3 py-2 text-xs font-semibold text-orange-500 rounded-md border border-orange-500"
              >
                {copied ? "복사됨" : "복사"}
              </button>
            </div>

            <div className="pt-3 mt-3 space-y-1 text-sm border-t border-gray-100">
              <div className="flex justify-between">
                <span className="text-gray-600">예금주</span>
                <span className="font-medium">
                  {depositInfo.virtualAccountHolder ?? "-"}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">입금 금액</span>
                <span className="font-bold">
                  {(depositInfo.amount ?? 0).toLocaleString()}원
                </span>
              </div>
              {fmtDue(depositInfo.dtDepositDue) && (
                <div className="flex justify-between">
                  <span className="text-gray-600">입금 기한</span>
                  <span className="font-medium text-red-600">
                    {fmtDue(depositInfo.dtDepositDue)}
                  </span>
                </div>
              )}
              <div className="flex justify-between text-xs text-gray-500">
                <span>주문번호</span>
                <span className="font-mono">{depositInfo.orderId}</span>
              </div>
            </div>
          </div>

          <ul className="p-4 mb-4 space-y-1 text-xs leading-relaxed text-gray-600 bg-gray-50 rounded-lg">
            <li>· 입금 금액이 정확히 일치해야 자동으로 발급돼요.</li>
            <li>· 입금 확인까지 보통 1~2분 걸려요. 창을 닫아도 괜찮아요.</li>
            <li>· 입금 기한이 지나면 계좌가 만료되며, 다시 구매해 주셔야 해요.</li>
            <li>· 이 계좌는 이번 결제에만 쓰이는 일회용 계좌예요.</li>
            {/* <li>· 가상계좌 결제에 대한 취소는 관리자에게 직접 연락 바랍니다.</li> */}
          </ul>

          {/* 입금 전에는 이용권이 없으므로 예약 화면으로 보내지 않는다 */}
          <button
            onClick={() => navigate({ to: "/customer/layout/home", replace: true })}
            className="px-4 py-3 w-full font-medium text-white bg-orange-500 rounded-lg transition-colors hover:bg-orange-600"
          >
            홈으로 이동
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex justify-center items-center flex-auto bg-gray-50">
      <div className="p-8 w-full max-w-md bg-white rounded-lg shadow-lg">
        <div className="text-center">
          {/* 성공 아이콘 */}
          <div className="mb-6">
            <div className="flex justify-center items-center mx-auto w-16 h-16 bg-green-100 rounded-full">
              <svg
                className="w-8 h-8 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
          </div>

          <h1 className="mb-4 text-2xl font-bold text-gray-900">
            결제가 완료되었습니다!
          </h1>
          <p className="mb-6 text-gray-600">
            이용권 구매가 성공적으로 처리되었습니다.
          </p>
          <div className="p-4 mb-6 text-left bg-gray-50 rounded-lg">
            <h3 className="mb-2 font-semibold text-gray-900">결제 정보</h3>
            <div className="space-y-1 text-sm text-gray-600">
              {paymentInfo ? (
                <>
                  <div>주문번호: {paymentInfo.orderId}</div>
                  <div>결제금액: {paymentInfo.amount.toLocaleString()}원</div>
                </>
              ) : (
                <div>결제 정보 없음</div>
              )}
            </div>
          </div>
          <div className="space-y-3">
            {paymentInfo?.companySeq ? (
              // companySeq가 있는 경우 예약하기 버튼
              <button
                onClick={() =>
                  navigate({
                    to: "/customer/layout/reservation-with-subscription",
                    search: {
                      // 문자열로 넘기면 JSON 직렬화로 URL이 %22..%22 가 되므로 숫자로 전달
                      companySeq: Number(
                        paymentInfo.companySeq
                      ) as unknown as string,
                      restoreState: paymentInfo.restoreState || undefined,
                      // 예약 화면의 뒤로가기를 매장 상세로 보내기 위한 진입 표시
                      fromPayment: "1",
                    },
                    // 결제 완료 화면은 히스토리에서 제거 — 되돌아오면 order-confirm 이
                    // 재호출돼 결제 실패 화면으로 튄다
                    replace: true,
                  })
                }
                className="px-4 py-3 w-full font-medium text-white bg-orange-500 rounded-lg transition-colors hover:bg-orange-600"
              >
                예약하기
              </button>
            ) : (
              // companySeq가 없는 경우 홈으로 이동
              <button
                onClick={() =>
                  navigate({ to: "/customer/layout/home", replace: true })
                }
                className="px-4 py-3 w-full font-medium text-white bg-orange-500 rounded-lg transition-colors hover:bg-orange-600"
              >
                홈으로 이동
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
